import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { format, parseISO } from "date-fns";
import { formatAmount } from "@utils";

// Brand palette (primary orange, teal, then distinct fallbacks) so multi-series comparisons read clearly.
const SERIES_COLORS = ["#EA4D0C", "#01787C", "#7C3AED", "#0891B2", "#CA8A04"];

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

// The model sends the chart as parallel series ([{ name, points: [{ x, y }] }]); recharts wants one row
// per x keyed by series name. Pivot into rows, keeping x-axis order as first encountered across series.
export function toRows(series) {
  const order = [];
  const rows = new Map();
  for (const s of series ?? []) {
    for (const point of s.points ?? []) {
      if (!rows.has(point.x)) {
        order.push(point.x);
        rows.set(point.x, { x: point.x });
      }
      rows.get(point.x)[s.name] = point.y ?? null;
    }
  }
  return order.map((x) => rows.get(x));
}

export function formatValue(value, yFormat) {
  if (value === null || value === undefined) return "";
  switch (yFormat) {
    case "currency":
      return formatAmount(value);
    case "percent":
      return `${Number(value).toLocaleString("en-GB", { maximumFractionDigits: 1 })}%`;
    case "days":
      return `${Number(value).toLocaleString("en-GB", { maximumFractionDigits: 0 })}d`;
    default:
      return Number(value).toLocaleString("en-GB", { maximumFractionDigits: 2 });
  }
}

// Adds a numeric timestamp per row and sorts chronologically, so a date line chart can use a real time
// scale (points spaced by actual date) rather than a category scale (every point equal-width, which makes
// unevenly-sampled dates look bunched).
export function toTimeData(rows) {
  return rows
    .map((row) => ({ ...row, __ts: parseISO(row.x).getTime() }))
    .sort((a, b) => a.__ts - b.__ts);
}

// Evenly-spaced tick positions across a range. Recharts otherwise ticks at the (uneven) data points on a
// time axis; passing these keeps the calendar spacing regular regardless of where the points fall.
export function evenTicks(min, max, count = 6) {
  if (!(max > min) || count < 2) return [min];
  const step = (max - min) / (count - 1);
  return Array.from({ length: count }, (_, i) => Math.round(min + i * step));
}

export default function ChartCard({ spec }) {
  const { type, title, xLabel, yLabel, yFormat, series = [] } = spec ?? {};
  const rows = toRows(series);
  const seriesNames = series.map((s) => s.name);
  const allDates = rows.length > 0 && rows.every((row) => ISO_DATE.test(row.x));

  // A line over dates gets a time axis; categorical charts (aged buckets, comparisons) keep the category axis.
  const timeAxis = type === "line" && allDates;
  const data = timeAxis ? toTimeData(rows) : rows;
  const minTs = timeAxis ? data[0].__ts : 0;
  const maxTs = timeAxis ? data[data.length - 1].__ts : 0;

  const xAxis = timeAxis ? (
    <XAxis
      dataKey="__ts"
      type="number"
      scale="time"
      domain={[minTs, maxTs]}
      ticks={evenTicks(minTs, maxTs, 6)}
      tickFormatter={(ts) => format(new Date(ts), "d MMM")}
      tick={{ fontSize: 11, fill: "#71717a" }}
      tickLine={false}
      axisLine={{ stroke: "#e4e4e7" }}
      label={xLabel ? { value: xLabel, position: "insideBottom", offset: -2, fontSize: 11, fill: "#a1a1aa" } : undefined}
    />
  ) : (
    <XAxis
      dataKey="x"
      tick={{ fontSize: 11, fill: "#71717a" }}
      tickLine={false}
      axisLine={{ stroke: "#e4e4e7" }}
      label={xLabel ? { value: xLabel, position: "insideBottom", offset: -2, fontSize: 11, fill: "#a1a1aa" } : undefined}
    />
  );

  const axes = (
    <>
      <CartesianGrid strokeDasharray="3 3" stroke="#f4f4f5" vertical={false} />
      {xAxis}
      <YAxis
        tickFormatter={(value) => formatValue(value, yFormat)}
        tick={{ fontSize: 11, fill: "#71717a" }}
        tickLine={false}
        axisLine={false}
        width={yFormat === "currency" ? 72 : 48}
      />
      <Tooltip
        formatter={(value) => formatValue(value, yFormat)}
        labelFormatter={timeAxis ? (ts) => format(new Date(ts), "d MMM yyyy") : undefined}
        contentStyle={{ borderRadius: 12, border: "1px solid #e4e4e7", fontSize: 12 }}
      />
      {seriesNames.length > 1 && <Legend wrapperStyle={{ fontSize: 12 }} />}
    </>
  );

  return (
    <div className="rounded-2xl border border-zinc-200 bg-white p-4">
      {title && (
        <div className="mb-2 text-xs font-medium uppercase tracking-wide text-zinc-500">{title}</div>
      )}
      <div className="flex">
        {yLabel && (
          <div className="flex items-center pr-1">
            <span className="rotate-180 text-[11px] text-zinc-400 [writing-mode:vertical-rl]">{yLabel}</span>
          </div>
        )}
        <div className="min-w-0 flex-1">
      <ResponsiveContainer width="100%" height={220}>
        {type === "line" ? (
          <LineChart data={data} margin={{ top: 8, right: 12, bottom: xLabel ? 16 : 4, left: 8 }}>
            {axes}
            {seriesNames.map((name, i) => (
              <Line
                key={name}
                type="monotone"
                dataKey={name}
                stroke={SERIES_COLORS[i % SERIES_COLORS.length]}
                strokeWidth={2}
                dot={false}
                connectNulls={false}
              />
            ))}
          </LineChart>
        ) : (
          <BarChart data={data} margin={{ top: 8, right: 12, bottom: xLabel ? 16 : 4, left: 8 }}>
            {axes}
            {seriesNames.map((name, i) => (
              <Bar
                key={name}
                dataKey={name}
                fill={SERIES_COLORS[i % SERIES_COLORS.length]}
                stackId={type === "stacked_bar" ? "a" : undefined}
                radius={type === "stacked_bar" ? 0 : [4, 4, 0, 0]}
                maxBarSize={56}
              />
            ))}
          </BarChart>
        )}
      </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
